import { useEffect, useMemo } from 'react';
import {
  useTargetedModel,
  useWatchableValue,
  Watchable,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import type { WebhookInstallStatus } from '@cotera/client/app/v1/webhook-providers/types';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import type { WebhookTriggerInstallState } from './workflow-node-models';
import { WORKFLOW_RENDER_NODE_KEYS } from './workflow-render-catalog';
import { WorkflowModel } from './workflow.model';
import type { WorkflowWebhookTriggerNodeModel } from './workflow-node-models/webhook-trigger-node.types';

type UseWebhookTriggerInstallStateParams = {
  triggerId: string | undefined;
  isDraft?: boolean;
  fallbackInstallStatus?: WebhookInstallStatus;
};

function draftInstallState(
  installStatus: WebhookInstallStatus
): WebhookTriggerInstallState {
  return {
    installStatus,
    installError: null,
    loaded: true,
  };
}

/**
 * Resolves live webhook install state for a trigger. Uses the workflow's
 * per-node view model when available; otherwise falls back to a standalone
 * event watchable (same factory path, no WorkflowModel required).
 */
export function useWebhookTriggerInstallState(
  params: UseWebhookTriggerInstallStateParams
): WebhookTriggerInstallState {
  const workflowModel = useTargetedModel(WorkflowModel);
  const factory = useWatchablesFactory();
  const client = useTenantedClient();
  const triggerId = params.triggerId;
  const isDraft =
    params.isDraft === true || triggerId === undefined || triggerId === '';

  const watchable = useMemo<
    ReadonlyWatchable<WebhookTriggerInstallState>
  >(() => {
    if (triggerId === undefined || triggerId === '') {
      return Watchable.fromValue(
        draftInstallState(params.fallbackInstallStatus ?? 'draft')
      );
    }
    if (workflowModel !== null) {
      return workflowModel.getRenderableNodeModel<WorkflowWebhookTriggerNodeModel>(
        WORKFLOW_RENDER_NODE_KEYS.webhookTrigger,
        triggerId
      ).installStateWatchable;
    }
    if (isDraft) {
      return Watchable.fromValue(
        draftInstallState(params.fallbackInstallStatus ?? 'draft')
      );
    }
    return factory.makeWebhookTriggerInstallStateWatchable({
      triggerId,
      client,
      initialValue: draftInstallState(params.fallbackInstallStatus ?? 'draft'),
    });
  }, [
    workflowModel,
    factory,
    client,
    isDraft,
    triggerId,
    params.fallbackInstallStatus,
  ]);

  useEffect(() => {
    if (workflowModel !== null || isDraft) {
      return;
    }
    const eventWatchable =
      watchable as ReadonlyEventWatchable<WebhookTriggerInstallState>;
    return () => {
      eventWatchable.unsubscribe();
    };
  }, [watchable, isDraft, workflowModel]);

  return useWatchableValue(watchable);
}
